import {
  Box,
  Divider,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Text as UiText } from '@diamond/shared/ui';
import { formatDateRange } from '@diamond/shared/utils';
import { ConfirmationNumberOutlined, ErrorOutline } from '@mui/icons-material';
import { UseFormReturn } from 'react-hook-form';

import { useVoucherPreview } from '../hooks';
import type { CreateVoucherRequest } from '../types';

interface VoucherPreviewProps {
  form: UseFormReturn<CreateVoucherRequest>;
}

const SECTION_SPACING = 4;

export function VoucherPreview({ form }: VoucherPreviewProps) {
  const { formData, bulletItems } = useVoucherPreview({ form });

  return (
    <VStack spacing={4} align="stretch">
      <UiText fontSize="lg" fontWeight="semibold">
        Preview Detail Voucher
      </UiText>

      <Box
        bg="white"
        borderRadius="16px"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="lg"
        maxW="400px"
        alignSelf="center"
      >
        {formData.banner_url ? (
          <Box position="relative" h="200px">
            <Image
              src={formData.banner_url}
              alt="Voucher Banner"
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>
        ) : null}

        <VStack spacing={SECTION_SPACING} align="stretch" p={6}>
          {formData.title ? (
            <Text fontWeight="bold" fontSize="xl" color="gray.900">
              {formData.title}
            </Text>
          ) : (
            <Box bg="gray.200" h="6" w="64" borderRadius="md" />
          )}

          {formData.desc ? (
            <Text fontSize="sm" lineHeight="1.5" color="gray.700">
              {formData.desc}
            </Text>
          ) : (
            <VStack spacing={2} align="stretch">
              <Box bg="gray.200" h="3" w="full" borderRadius="md" />
              <Box bg="gray.200" h="3" w="3/4" borderRadius="md" />
            </VStack>
          )}

          {formData.code ? (
            <Box
              bg="rgba(0, 122, 255, 0.05)"
              borderRadius="lg"
              px={4}
              py={3}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <HStack spacing={2}>
                <ConfirmationNumberOutlined
                  sx={{ fontSize: '18px', color: '#007AFF' }}
                />
                <Text fontSize="lg" fontWeight="semibold" color="#007AFF">
                  {formData.code}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600">
                {formData.period_start_date && formData.period_end_date
                  ? formatDateRange(
                      new Date(formData.period_start_date),
                      new Date(formData.period_end_date)
                    )
                  : '-'}
              </Text>
            </Box>
          ) : null}

          <Divider borderColor="gray.100" />

          <VStack align="stretch" spacing={3}>
            <HStack spacing={2} align="start">
              <Icon as={ErrorOutline} color="orange.400" boxSize={4} mt={1} />
              <Text fontWeight="semibold" fontSize="sm" color="gray.900">
                Syarat dan ketentuan penggunaan voucher
              </Text>
            </HStack>

            <VStack spacing={2} align="stretch" pl={6}>
              {bulletItems.map((item) => (
                <HStack key={item} align="flex-start" spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    •
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    {item}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
}
